import ActionCable from 'actioncable';
import { useEffect, useRef, useState } from 'react';
import { makeGETRequest } from './Axios';

// Channel tokens have a 24h TTL (see ChannelTokenService).
const DEFAULT_REFRESH_INTERVAL_MINUTES = 12 * 60;
const INITIAL_RETRY_DELAY_MS = 5000;
const MAX_RETRY_DELAY_MS = 60000;

// Owns an authenticated ActionCable consumer for the given workspace.
//
// The consumer is state, not a ref: subscribers list it in their effect deps,
// so replacing it has to trigger a render. Plain reconnects are left to
// ActionCable's ConnectionMonitor — only a token refresh rebuilds the consumer,
// because a new token means a new connection URL.
function useSecureCable({
    refreshUrl,
    refreshIntervalMinutes = DEFAULT_REFRESH_INTERVAL_MINUTES,
    workspace_id = null
}) {
    const [cable, setCable] = useState(null);

    // Cleanup runs on unmount and on a deps change; only the former must skip
    // the state update.
    const mountedRef = useRef(true);
    useEffect(() => () => { mountedRef.current = false; }, []);

    useEffect(() => {
        let cancelled = false;
        let currentCable = null;
        let retryTimeout = null;
        let retryDelay = INITIAL_RETRY_DELAY_MS;
        let refreshInterval = null;

        const tokenUrl = workspace_id
            ? `${refreshUrl}?workspace_id=${workspace_id}`
            : refreshUrl;

        const fetchToken = async () => {
            try {
                const response = await makeGETRequest(tokenUrl);
                if (response.data.status !== 'ok') throw new Error('Failed to fetch token');

                return response.data.token;
            } catch (error) {
                console.error('❌ Error fetching token for Action Cable:', error);
                return null;
            }
        };

        // A user who lost access never gets a token; back off instead of
        // hammering the endpoint.
        const scheduleRetry = () => {
            if (cancelled) return;

            clearTimeout(retryTimeout);
            retryTimeout = setTimeout(connect, retryDelay);
            retryDelay = Math.min(retryDelay * 2, MAX_RETRY_DELAY_MS);
        };

        const connect = async () => {
            const token = await fetchToken();
            if (cancelled) return;

            if (!token) {
                console.warn('[Cable] Cannot connect — no token available.');
                scheduleRetry();
                return;
            }

            retryDelay = INITIAL_RETRY_DELAY_MS;

            const previousCable = currentCable;
            currentCable = ActionCable.createConsumer(
                `${process.env.REACT_APP_CABLE_URL}?token=${token}`
            );

            console.info('[Cable] Consumer ready');

            // Publish the replacement before dropping the old one.
            setCable(currentCable);
            previousCable?.disconnect();
        };

        connect();
        refreshInterval = setInterval(connect, refreshIntervalMinutes * 60 * 1000);

        return () => {
            cancelled = true;
            clearTimeout(retryTimeout);
            clearInterval(refreshInterval);
            currentCable?.disconnect();
            if (mountedRef.current) setCable(null);
        };
    }, [refreshUrl, refreshIntervalMinutes, workspace_id]);

    return cable;
}

export default useSecureCable;
